import anime from 'animejs';
import { Fragment, type HTMLAttributes } from 'react';
import { useInViewEffect } from 'react-hook-inview';

import cn from '@/helper/cn';

import Card from '../components/card';
import type { CardType } from '../constants/solutions';

type ProvideSectionType = {
  cards: CardType[];
  title: string;
  infoSummary: string;
} & HTMLAttributes<HTMLDivElement>;

const ProvideSection = ({
  cards,
  title,
  infoSummary,
  className,
  ...rest
}: ProvideSectionType) => {
  const sectionRef = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);

        anime
          .timeline({ loop: false })
          .add({
            targets: 'div > h2',
            opacity: [0, 1],
            translateX: [100, 0],
            duration: 1000,
            easing: 'easeOutExpo',
          })
          .add(
            {
              targets: '#provide-section-description',
              opacity: [0, 1],
              translateX: [100, 0],
              duration: 1000,
              easing: 'easeOutExpo',
            },
            '-=750',
          )
          .add(
            {
              targets: '.card',
              opacity: [0, 1],
              translateY: [100, 0],
              duration: 1000,
              easing: 'easeOutExpo',
              delay: anime.stagger(100),
            },
            '-=750',
          );
      }
    },
    { threshold: 0.2 },
  );
  return (
    <section
      ref={sectionRef}
      id="provide-section"
      className="w-full bg-solutions"
    >
      <div
        className={cn(
          'relative isolate mx-auto flex max-w-container flex-col items-start justify-center gap-y-8 overflow-hidden px-10 py-10 lg:px-20 lg:py-20',
          className,
        )}
        {...rest}
      >
        <div className="flex w-full lg:w-1/2">
          <div className="mx-auto flex flex-col gap-4">
            <h2 className="whitespace-pre-line text-xl font-semibold text-zinc-900 opacity-0 [text-wrap:balance] sm:text-2xl lg:text-4xl/relaxed">
              {title}
            </h2>
            <p
              id="provide-section-description"
              className="max-w-xl text-xs leading-relaxed text-zinc-600 text-opacity-70 [text-wrap:balance] sm:text-base lg:text-lg"
            >
              {infoSummary}
            </p>
          </div>
        </div>
        <div className="sm:max-lg:max-w[75%] flex flex-col gap-10 sm:max-lg:mx-auto lg:flex-row lg:gap-4">
          {cards.map((card, index) => (
            <Fragment key={`fragment-${index}`}>
              <Card
                key={`card-${index}-mobile`}
                Icon={() => <card.Icon className="max-sm:h-11 max-sm:w-11" />}
                title={card.title}
                content={card.content}
                className="card"
              />
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProvideSection;
